/**
 * Displays a meaning's relations to grammemes (meaning_grammeme).
 * Shows grammatical_feature and grammeme value for each.
 * When editable, the value is a dropdown to change the grammeme (no remove button).
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t } from '../utils/i18n.js'
import '@material/web/button/filled-button.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'
import styles from './meaning-grammeme-display.css?inline'

const GRAMMEMES_QUERY = gql`
  query GrammemesForDisplay {
    grammemes {
      id
      value
      feature { id name }
    }
  }
`

const MEANING_GRAMMEMES_QUERY = gql`
  query MeaningGrammemesForDisplay($meaningId: ID!) {
    meaningGrammemes(meaningId: $meaningId) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

const UPDATE_MEANING_GRAMMEME_MUTATION = gql`
  mutation UpdateMeaningGrammeme($input: UpdateMeaningGrammemeInput!) {
    updateMeaningGrammeme(input: $input) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

export interface MeaningGrammemeDisplayItem {
  id: string
  grammeme: {
    id: string
    value: string
    feature: {
      id: string
      name: string
    }
  }
}

interface GrammemeOption {
  id: string
  value: string
  feature: { id: string; name: string }
}

@customElement('meaning-grammeme-display')
export class MeaningGrammemeDisplay extends LitElement {
  /** List of meaning_grammeme items to display (with grammeme and feature) */
  @property({ type: Array }) items: MeaningGrammemeDisplayItem[] = []

  /** When true, show a remove button on each row (only when not editable) */
  @property({ type: Boolean }) removable = false

  /** When true, show a dropdown to change the grammeme value instead of static text and no remove button */
  @property({ type: Boolean }) editable = false

  /** When set, component fetches and refetches meaning grammemes for this meaning (so list updates after add/update) */
  @property({ type: String }) meaningId = ''

  /** Increment from parent to request a refetch (e.g. after add); display refetches when this changes */
  @property({ type: Number }) refreshTrigger = 0

  /** ID of the item currently being deleted (disables its remove button) */
  @property({ type: String }) deletingId = ''

  @state() private grammemes: GrammemeOption[] = []
  @state() private grammemesLoaded = false
  @state() private updatingId = ''
  /** When editable, pending dropdown selection per row id (only set when user changes selection) */
  @state() private selectedByRowId: Record<string, string> = {}
  /** Fetched list when meaningId is set (refetched on created/updated) */
  @state() private fetchedItems: MeaningGrammemeDisplayItem[] = []

  static styles = css`
    ${unsafeCSS(styles)}
  `

  connectedCallback() {
    super.connectedCallback()
    if (this.editable) this.loadGrammemes()
    if (this.meaningId) this.refetchMeaningGrammemes()
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('editable') && this.editable && !this.grammemesLoaded) this.loadGrammemes()
    if ((changed.has('meaningId') || changed.has('refreshTrigger')) && this.meaningId) this.refetchMeaningGrammemes()
  }

  /** Refetch meaning grammemes for current meaningId (network-only so new/updated items appear). */
  async refetchMeaningGrammemes() {
    if (!this.meaningId) return
    try {
      const res = await apolloClient.query({
        query: MEANING_GRAMMEMES_QUERY,
        variables: { meaningId: this.meaningId },
        fetchPolicy: 'network-only',
      })
      const all = (res.data as any).meaningGrammemes ?? []
      this.fetchedItems = all.map((mg: any) => ({
        id: mg.id,
        grammeme: {
          id: mg.grammeme?.id ?? '',
          value: mg.grammeme?.value ?? '',
          feature: { id: mg.grammeme?.feature?.id ?? '', name: mg.grammeme?.feature?.name ?? '' },
        },
      }))
    } catch {
      // ignore
    }
  }

  private async loadGrammemes() {
    try {
      const res = await apolloClient.query({ query: GRAMMEMES_QUERY })
      this.grammemes = (res.data as any).grammemes ?? []
    } finally {
      this.grammemesLoaded = true
    }
  }

  private handleRemove(e: Event) {
    const item = (e.target as HTMLElement).closest?.('[data-id]') as HTMLElement
    const id = item?.dataset?.id
    if (id) {
      this.dispatchEvent(new CustomEvent('meaning-grammeme-remove', {
        detail: { id },
        bubbles: true,
        composed: true,
      }))
    }
  }

  private grammemesForFeature(featureId: string): GrammemeOption[] {
    return this.grammemes.filter((g) => g.feature?.id === featureId)
  }

  private setDropdownSelection(itemId: string, grammemeId: string) {
    this.selectedByRowId = { ...this.selectedByRowId, [itemId]: grammemeId }
  }

  private async handleUpdateClick(item: MeaningGrammemeDisplayItem) {
    const newGrammemeId = this.selectedByRowId[item.id] ?? item.grammeme.id
    if (newGrammemeId === item.grammeme.id) return
    this.updatingId = item.id
    try {
      await apolloClient.mutate({
        mutation: UPDATE_MEANING_GRAMMEME_MUTATION,
        variables: {
          input: { id: item.id, grammemeId: newGrammemeId },
        },
      })
      const { [item.id]: _, ...rest } = this.selectedByRowId
      this.selectedByRowId = rest
      await this.refetchMeaningGrammemes()
      this.dispatchEvent(new CustomEvent('meaning-grammeme-updated', { bubbles: true, composed: true }))
    } finally {
      this.updatingId = ''
    }
  }

  private featureLabel(featureId: string, featureName: string): string {
    const key = `grammatical_feature_${featureId}`
    const out = t(`${key}.name`)
    return out === `${key}.name` ? featureName : out
  }

  private grammemeLabel(grammemeId: string, fallbackValue: string): string {
    const out = t(`grammeme_${grammemeId}.value`)
    return out === `grammeme_${grammemeId}.value` ? fallbackValue : out
  }

  render() {
    const list = this.meaningId
      ? (this.fetchedItems.length ? this.fetchedItems : this.items)
      : this.items
    if (!list?.length) return html``

    return html`
      <div class="meaning-grammeme-list">
        ${list.map((item) => {
          const featureId = item.grammeme.feature?.id ?? ''
          const options = this.editable ? this.grammemesForFeature(featureId) : []
          const showDropdown = this.editable && this.grammemesLoaded && options.length > 0
          return html`
          <div class="meaning-grammeme-row" data-id="${item.id}">
            <span class="feature-name">${this.featureLabel(featureId, item.grammeme.feature?.name ?? '')}</span>
            ${showDropdown ? html`
              <md-filled-select
                class="grammeme-select"
                .value=${this.selectedByRowId[item.id] ?? item.grammeme.id}
                ?disabled=${this.updatingId === item.id}
                @change=${(e: Event) => this.setDropdownSelection(item.id, (e.target as HTMLSelectElement).value)}
              >
                ${options.map((g) => html`
                  <md-select-option value=${g.id}>
                    <div slot="headline">${this.grammemeLabel(g.id, g.value)}</div>
                  </md-select-option>
                `)}
              </md-filled-select>
              <md-filled-button
                class="update-btn"
                ?disabled=${this.updatingId === item.id || (this.selectedByRowId[item.id] ?? item.grammeme.id) === item.grammeme.id}
                @click=${() => this.handleUpdateClick(item)}
              >
                ${this.updatingId === item.id ? t('editDialog.updating') : t('editDialog.update')}
              </md-filled-button>
              <md-filled-button
                class="remove-btn"
                ?disabled=${this.deletingId === item.id}
                @click=${this.handleRemove}
              >
                ${t('common.delete')}
              </md-filled-button>
            ` : html`
              <span class="grammeme-value">${this.grammemeLabel(item.grammeme.id, item.grammeme.value)}</span>
            `}
            ${!this.editable && this.removable ? html`
              <md-filled-button
                class="remove-btn"
                ?disabled=${this.deletingId === item.id}
                @click=${this.handleRemove}
              >
                ${t('common.delete')}
              </md-filled-button>
            ` : ''}
          </div>
        `})}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'meaning-grammeme-display': MeaningGrammemeDisplay
  }
}
